import React, { useEffect, useMemo, useState } from "react";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";
import { getUiText } from "../../../helpers/localizationHelpers";
import {
  buildDefaultNextStepsFaqConfig,
  stripNextStepsFaqSystemFields
} from "../../../helpers/nextStepsFaqHelpers";

const parseRouteTables = (routeTablesDraft) => {
  const raw = String(routeTablesDraft || "").trim();
  if (!raw) return { ok: true, rows: [], error: "" };

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return { ok: false, rows: [], error: "Route tables JSON must be an array." };
    }
    return { ok: true, rows: parsed, error: "" };
  } catch {
    return { ok: false, rows: [], error: "Route tables JSON is invalid." };
  }
};

const getRoutePath = (row) =>
  Array.isArray(row?.path) ? row.path.join(" -> ") : String(row?.route || row?.path || "");

const parseDurationMinutes = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "-") return null;

  let minutes = 0;
  const hourMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:hr|hrs|hour|hours|h)\b/);
  const minMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:min|mins|minute|minutes|m)\b/);
  if (hourMatch) minutes += Number(hourMatch[1]) * 60;
  if (minMatch) minutes += Number(minMatch[1]);

  if (!hourMatch && !minMatch) {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) minutes = numeric;
  }

  return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null;
};

const formatDurationMinutes = (minutes) => {
  const mins = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  const hourLabel = hours === 1 ? "hour" : "hours";
  if (hours && remainder) return `${hours} ${hourLabel} ${remainder} min`;
  if (hours) return `${hours} ${hourLabel}`;
  return `${remainder} min`;
};

const getLateReachTime = (row) => {
  const raw = row?.leave10MinLate || row?.late10min || row?.late_10_min || "";
  const minutes = parseDurationMinutes(raw);
  return minutes == null ? raw || "-" : `Reach at ${formatDurationMinutes(minutes + 10)}`;
};

const buildTimesTableBody = (rows) => [
  [
    { text: "Route", style: "tableHeader" },
    { text: "Exit", style: "tableHeader" },
    { text: "Leave on time", style: "tableHeader" },
    { text: "Leave 10 min late", style: "tableHeader" },
    { text: "Free-flow", style: "tableHeader" }
  ],
  ...rows.map((row) => [
    getRoutePath(row) || "-",
    row?.exitName || row?.exit || row?.exit_name || "-",
    row?.leaveOnTime || row?.onTime || row?.on_time || "-",
    getLateReachTime(row),
    row?.fft || "-"
  ])
];

const buildPdfDefinition = ({ disclaimer, routeRows, faqItems }) => {
  const content = [
    { text: "WUI-Cast Simulation data", style: "title" },
    { text: disclaimer.title || "Disclaimer", style: "sectionHeader" },
    {
      ul: (disclaimer.bullets || []).map((item) => String(item || "")),
      margin: [0, 0, 0, 16]
    },
    { text: "Evacuation Times", style: "sectionHeader" }
  ];

  if (routeRows.length) {
    content.push({
      table: {
        headerRows: 1,
        widths: [150, "*", "auto", "auto", "auto"],
        body: buildTimesTableBody(routeRows)
      },
      layout: "lightHorizontalLines",
      margin: [0, 0, 0, 16]
    });
  } else {
    content.push({ text: "No route times are available for this node.", margin: [0, 0, 0, 16] });
  }

  content.push({ text: "FAQs", style: "sectionHeader" });

  if (faqItems.length) {
    faqItems.forEach((item) => {
      content.push({ text: String(item?.q || "Question"), style: "question" });
      content.push({ text: String(item?.a || ""), margin: [0, 2, 0, 10] });
    });
  } else {
    content.push({ text: "No FAQs are configured for this language." });
  }

  return {
    pageSize: "LETTER",
    pageMargins: [40, 44, 40, 44],
    content,
    styles: {
      title: { fontSize: 22, bold: true, margin: [0, 0, 0, 8] },
      sectionHeader: { fontSize: 15, bold: true, margin: [0, 12, 0, 8] },
      question: { bold: true, margin: [0, 6, 0, 0] },
      tableHeader: { bold: true, fillColor: "#eeeeee" }
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.25
    }
  };
};

const createPdfBlob = async (docDefinition) => {
  const [{ default: pdfMake }, pdfFonts] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts")
  ]);
  pdfMake.vfs = pdfFonts.default || pdfFonts;

  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBlob(resolve);
    } catch (err) {
      reject(err);
    }
  });
};

const DownloadablePdfSection = ({ podcastId, selectedLanguage, nodeId, routeTablesDraft }) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const langCode = String(selectedLanguage || "EN").toUpperCase();
  const parsedRoutes = useMemo(() => parseRouteTables(routeTablesDraft), [routeTablesDraft]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const storagePath = useMemo(() => {
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();
    if (!pid || !langCode || !nid) return "";
    return `Podcasts/${pid}/${langCode}/${nid}/evac_times.pdf`;
  }, [podcastId, langCode, nodeId]);

  const metadataRef = useMemo(() => {
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();
    if (!pid || !langCode || !nid) return null;
    return doc(db, "podcasts", pid, "podcast_details", nid, langCode, "podcastMetadata");
  }, [db, podcastId, langCode, nodeId]);

  useEffect(() => {
    if (!storagePath) {
      setPdfUrl("");
      return;
    }

    let cancelled = false;
    setStatus("");
    setError("");
    setPdfUrl("");

    getDownloadURL(storageRef(storage, storagePath))
      .then((url) => {
        if (!cancelled) setPdfUrl(url);
      })
      .catch((err) => {
        if (err?.code !== "storage/object-not-found") {
          console.error("Failed to load existing evacuation PDF:", err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [storage, storagePath]);

  const loadFaqItems = async () => {
    const defaultConfig = buildDefaultNextStepsFaqConfig(langCode);
    if (!podcastId) return (defaultConfig.faqItems || []).filter((item) => item?.pdf === true);

    const snap = await getDoc(doc(db, "podcasts", String(podcastId), "next-steps-faq", langCode));
    if (!snap.exists()) return (defaultConfig.faqItems || []).filter((item) => item?.pdf === true);

    const config = stripNextStepsFaqSystemFields(snap.data());
    const faqItems = Array.isArray(config?.faqItems) ? config.faqItems : defaultConfig.faqItems || [];
    return faqItems.filter((item) => item?.pdf === true);
  };

  const handleCreatePdf = async () => {
    if (!storagePath) {
      setError("Pick a node before creating the PDF.");
      return;
    }
    if (!parsedRoutes.ok) {
      setError(parsedRoutes.error);
      return;
    }

    try {
      setIsGenerating(true);
      setStatus("");
      setError("");
      setPdfUrl("");

      const uiText = getUiText(langCode);
      const faqItems = await loadFaqItems();
      const docDefinition = buildPdfDefinition({
        podcastId,
        nodeId,
        langCode,
        disclaimer: {
          title: uiText.disclaimerTitle,
          bullets: uiText.disclaimerBullets || []
        },
        routeRows: parsedRoutes.rows,
        faqItems
      });
      const blob = await createPdfBlob(docDefinition);
      const fileRef = storageRef(storage, storagePath);
      await uploadBytes(fileRef, blob, { contentType: "application/pdf" });
      if (metadataRef) {
        await setDoc(metadataRef, { pdfCreated: true }, { merge: true });
      }
      const url = await getDownloadURL(fileRef);
      setPdfUrl(url);
      setStatus("PDF created and uploaded.");
    } catch (err) {
      console.error("Failed to create evacuation PDF:", err);
      setError("Failed to create PDF.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRemovePdf = async () => {
    if (!storagePath) {
      setError("Pick a node before removing the PDF.");
      return;
    }

    try {
      setIsRemoving(true);
      setStatus("");
      setError("");

      await deleteObject(storageRef(storage, storagePath)).catch((err) => {
        if (err?.code !== "storage/object-not-found") throw err;
      });

      if (metadataRef) {
        await setDoc(metadataRef, { pdfCreated: false }, { merge: true });
      }

      setPdfUrl("");
      setStatus("PDF removed.");
    } catch (err) {
      console.error("Failed to remove evacuation PDF:", err);
      setError("Failed to remove PDF.");
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
      <h3 style={{ margin: 0, marginBottom: 10 }}>Create Downloadable PDF</h3>
      <div style={{ color: "#4b5563", fontSize: 13, marginBottom: 10 }}>
        Creates a PDF for the currently selected node and uploads it to{" "}
        <code>{storagePath || "Podcasts/{podcastId}/{LANG}/{nodeId}/evac_times.pdf"}</code>.
      </div>

      {!parsedRoutes.ok && (
        <div style={{ color: "#b91c1c", fontWeight: 700, marginBottom: 10 }}>
          {parsedRoutes.error}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button
          type="button"
          onClick={handleCreatePdf}
          disabled={isGenerating || isRemoving || !storagePath || !parsedRoutes.ok}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "none",
            background: !isGenerating && !isRemoving && storagePath && parsedRoutes.ok ? "#7c3aed" : "#94a3b8",
            color: "#fff",
            fontWeight: 700,
            cursor: !isGenerating && !isRemoving && storagePath && parsedRoutes.ok ? "pointer" : "not-allowed"
          }}
        >
          {isGenerating ? "Creating PDF..." : "Create Downloadable PDF"}
        </button>

        {pdfUrl && (
          <button
            type="button"
            onClick={handleRemovePdf}
            disabled={isGenerating || isRemoving || !storagePath}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "1px solid #dc2626",
              background: "#fff",
              color: "#dc2626",
              fontWeight: 700,
              cursor: !isGenerating && !isRemoving && storagePath ? "pointer" : "not-allowed"
            }}
          >
            {isRemoving ? "Removing PDF..." : "Remove PDF"}
          </button>
        )}
      </div>

      {status && <span style={{ marginLeft: 10, color: "#065f46", fontWeight: 700 }}>{status}</span>}
      {error && <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>{error}</div>}

      {pdfUrl && (
        <div style={{ marginTop: 14 }}>
          <div style={{ marginBottom: 8 }}>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              Open PDF in new tab
            </a>
          </div>
          <iframe
            title="Evacuation times PDF preview"
            src={pdfUrl}
            style={{
              width: "100%",
              minHeight: 520,
              border: "1px solid #d1d5db",
              borderRadius: 8,
              background: "#fff"
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DownloadablePdfSection;
